import { ReactNode, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import type { Location } from 'history';
import Modal from '../components/Modals/Modal/Modal';
import UnsavedChangesModalContent from '../components/Modals/UnsavedChangesModalContent/UnsavedChangesModalContent';
import { useModal } from './useModal';

type UseUnsavedChangesGuardArgs = {
  isDirty: boolean;
  onSave: () => Promise<boolean>;
  // Navigation whose pathname starts with this prefix bypasses the guard
  // (routes internal to the guarded page, e.g. a fullscreen reference).
  allowedPathPrefix?: string;
};

export type UseUnsavedChangesGuardReturn = {
  // Renders null when closed; place unconditionally in JSX.
  modalElement: ReactNode;
};

export const useUnsavedChangesGuard = ({
  isDirty,
  onSave,
  allowedPathPrefix,
}: UseUnsavedChangesGuardArgs): UseUnsavedChangesGuardReturn => {
  const history = useHistory();
  const modal = useModal();
  // Blocked navigation target; resumed after a successful save from the modal.
  const pendingLocationRef = useRef<Location | null>(null);
  // Active history blocker; released manually before resuming the navigation.
  const unblockRef = useRef<(() => void) | undefined>(undefined);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Releases the blocker and resumes the navigation the guard intercepted.
  const proceedToPendingLocation = (): void => {
    unblockRef.current?.();
    unblockRef.current = undefined;

    const target = pendingLocationRef.current;
    pendingLocationRef.current = null;

    if (target) {
      history.push(`${target.pathname}${target.search}${target.hash}`);
    }
  };

  const openUnsavedChangesModal = (): void => {
    modal.open(
      <UnsavedChangesModalContent
        onSave={async (): Promise<void> => {
          modal.close();
          const isSaved = await onSaveRef.current();
          if (isSaved) proceedToPendingLocation();
        }}
        onDiscard={(): void => {
          // Leave without saving — the changes die with the unmounted page.
          modal.close();
          proceedToPendingLocation();
        }}
        onCancel={(): void => {
          // Stay on the page with the changes untouched.
          modal.close();
          pendingLocationRef.current = null;
        }}
      />
    );
  };

  // In-app navigation guard.
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const unblock = history.block((location) => {
      if (
        allowedPathPrefix &&
        location.pathname.startsWith(allowedPathPrefix)
      ) {
        return;
      }

      pendingLocationRef.current = location;
      openUnsavedChangesModal();
      return false;
    });
    unblockRef.current = unblock;

    return () => {
      unblock();
      unblockRef.current = undefined;
    };
  }, [isDirty, allowedPathPrefix]);

  // Browser reload/close: only the native prompt is allowed here.
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const modalElement = modal.isOpen ? <Modal {...modal.modalProps} /> : null;

  return { modalElement };
};
